import classNames from "classnames";
import { Observer } from "mobx-react";
import React, { useEffect, useRef } from "react";
import { SkeletonComponent } from "@syncfusion/ej2-react-notifications";
import { GridMetricLayout, GridMetricOptionValueReadDTO } from "./types";
import GridMetricsApiClient from "./apiClients/gridMetricsApiClient";
import ApiClient from "../../../../../lib/apiClient";
import AppToasterV2 from "../../../../ToastV2/ToastV2";
import { PrimaryCard, SecondaryCard } from "../../../../Cards/";
import { ReadOnlyField, ReadOnlyFieldProps } from "../../../../Fields/";
import { get } from "lodash";
import { GridFilterSettings } from "../../types/GridFilterSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons";
import { SecondaryButton } from "../../../../Buttons";
import { openGridMetricsPickerDialog } from "./dialog/GridMetricsPickerDialog";

export interface GridMetricsProps<T> {
  gridKey: string;
  url: string;
  languageMapping: T;
  columns?: number;
  layout?: GridMetricLayout;
  minimal?: boolean;
  apiClient: ApiClient;
  filterSettings?: GridFilterSettings;
  getAuthRequestHeader?: () => Promise<Record<string, string>>;
  gridViewTitle?: string;
  showMetricViewPicker?: boolean;
  metricConfiguration: string[];
  viewId?: string;
  updateViewMetricSettings: (
    viewId: string,
    metricSettings: string[]
  ) => Promise<void>;
}
export const GridMetrics = <T extends object>(props: GridMetricsProps<T>) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [metrics, setMetrics] = React.useState<T>();
  const [queryKey, setQueryKey] = React.useState<string>();

  const gridMetricsApiClient = useRef<GridMetricsApiClient>(
    new GridMetricsApiClient(props.apiClient)
  );

  useEffect(() => {
    if (!!props.filterSettings?.rawQuery) {
      const requestKey = props.filterSettings?.rawQuery;
      if (requestKey !== queryKey) {
        setQueryKey(requestKey);
      }
    }
  }, [props.filterSettings?.rawQuery]);

  useEffect(() => {
    fetchMetrics();
  }, [queryKey]);

  const fetchMetrics = async () => {
    if (!queryKey) {
      return;
    }

    setLoading(true);

    // Attempt to retrieve auth headers (if supplied)
    let headers: Record<string, string> | undefined = undefined;
    if (props.getAuthRequestHeader) {
      headers = await props.getAuthRequestHeader();
    }

    const newMetrics = await gridMetricsApiClient.current
      .fetchMetrics<T>(
        `${props.url}/${props.gridKey}/metrics`,
        queryKey,
        props.gridViewTitle,
        headers
      )
      .catch(() => {
        AppToasterV2.showFailedActionToaster(
          "An error occurred loading the metrics data."
        );
      });

    setMetrics(newMetrics || undefined);
    setLoading(false);
  };

  const fetchMetricOptionValues = async () => {
    // Attempt to retrieve auth headers (if supplied)
    let headers: Record<string, string> | undefined = undefined;
    if (props.getAuthRequestHeader) {
      headers = await props.getAuthRequestHeader();
    }

    const metricOptionValues = await gridMetricsApiClient.current
      .fetchMetricOptionValues<GridMetricOptionValueReadDTO>(
        `${props.url}/${props.gridKey}/${props.gridViewTitle}/metric-option-values`,
        headers
      )
      .catch(() => {
        AppToasterV2.showFailedActionToaster(
          "An error occurred loading the metrics data."
        );
      });

    return metricOptionValues;
  };

  const isShowingMetrics =
    !props.showMetricViewPicker ||
    (props.metricConfiguration && props.metricConfiguration.length > 0);

  return (
    <Observer>
      {() => {
        return (
          <section
            className={classNames(
              "grid-metrics",
              {
                "grid-metrics-minimal": props.minimal,
                "grid-metrics-loading": loading,
              },
              isShowingMetrics ? "grid-metrics-expanded" : "grid-metrics-empty"
            )}
          >
            <PrimaryCard className="grid-metrics__card">
              <SecondaryCard
                columns={
                  props.columns ||
                  (metrics && Array.from(Object.entries(metrics))?.length)
                }
              >
                {loading &&
                  Array.from(Array(props.columns)).map((_x, idx) => {
                    return (
                      <div className="bp5-form-group" key={idx}>
                        <div className="bp5-form-content">
                          <SkeletonComponent
                            id="skeletonText"
                            shape="Rectangle"
                          />
                        </div>
                      </div>
                    );
                  })}
                {!loading &&
                  metrics &&
                  !props.showMetricViewPicker &&
                  Array.from(Object.entries(metrics)).map((metric, idx) => {
                    return (
                      <ReadOnlyField
                        key={idx}
                        {...({ value: metric[1] } as ReadOnlyFieldProps)}
                        minimal={true}
                        label={get(props.languageMapping, metric[0])}
                      />
                    );
                  })}
                {!loading &&
                  metrics &&
                  props.showMetricViewPicker &&
                  Array.from(Object.entries(metrics))
                    .filter(([key]) => props.metricConfiguration.includes(key))
                    .map((metric, idx) => {
                      return (
                        <ReadOnlyField
                          key={idx}
                          {...({ value: metric[1] } as ReadOnlyFieldProps)}
                          minimal={true}
                          label={get(props.languageMapping, metric[0])}
                        />
                      );
                    })}
              </SecondaryCard>
              {!loading && props.showMetricViewPicker && (
                <div className="grid-metrics__picker-button-container">
                  <SecondaryButton
                    duration={0}
                    onClick={() => {
                      openGridMetricsPickerDialog(
                        props.viewId || "",
                        props.updateViewMetricSettings,
                        fetchMetricOptionValues,
                        props.metricConfiguration
                      );
                    }}
                  >
                    <FontAwesomeIcon icon={faCog} />
                  </SecondaryButton>
                </div>
              )}
            </PrimaryCard>
          </section>
        );
      }}
    </Observer>
  );
};
